// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { cybersecurityAssuranceIndexWord } from '../constants/cybersecurity-assurance-index-words.js';
/** A constant factor written before the quotient: `100 × …`, `100*…`, `100 x …`, `100 · …`. */
const LEADING_FACTOR = /^\s*(\d+(?:\.\d+)?)\s*[×*xX·]\s*\S/u;
function factorOfText(text) {
    if (typeof text !== 'string' || text.trim() === '')
        return 'none';
    const m = LEADING_FACTOR.exec(text);
    if (!m)
        return 'none';
    const n = Number(m[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
}
/**
 * The constant factor the pack's formula states before its quotient: `100`
 * for `100 × a / b`; `1` when the formula states none (or there is no
 * formula); `null` when the EN and VI texts disagree, or state a factor that
 * is not a positive number — the value is then not derived.
 */
export function cybersecurityAssuranceIndexFormulaFactor(formula) {
    if (!formula)
        return 1;
    const en = factorOfText(formula.en);
    const vi = factorOfText(formula.vi);
    if (en === null || vi === null)
        return null;
    const a = en === 'none' ? 1 : en;
    const b = vi === 'none' ? 1 : vi;
    return a === b ? a : null;
}
/**
 * `factor × numerator ÷ denominator`, the factor the pack's formula states;
 * `null` with no denominator, a denominator of 0, or an unreadable factor.
 */
export function cybersecurityAssuranceIndexDerivedValue(numerator, denominator, formula) {
    if (denominator === null || denominator === undefined || denominator === 0)
        return null;
    const factor = cybersecurityAssuranceIndexFormulaFactor(formula);
    if (factor === null)
        return null;
    return (factor * numerator) / denominator;
}
/** A derived value in the locale's number format, at most two decimals. */
export function formatCybersecurityAssuranceIndexDerivedValue(value, locale) {
    return new Intl.NumberFormat(locale === 'vi' || locale.startsWith('vi-') ? 'vi-VN' : 'en-GB', {
        maximumFractionDigits: 2,
    }).format(value);
}
/**
 * The derived-value cell as a page and the export print it: the value then
 * *Derived on read: <formula>*, or the words for why no value is derived
 * (no denominator; a denominator of 0; a formula whose factor cannot be read).
 */
export function cybersecurityAssuranceIndexDerivedText(numerator, denominator, formula, locale) {
    if (denominator === null || denominator === undefined) {
        return cybersecurityAssuranceIndexWord('derivedNoDenominator', locale);
    }
    if (denominator === 0)
        return cybersecurityAssuranceIndexWord('derivedDenominatorZero', locale);
    const value = cybersecurityAssuranceIndexDerivedValue(numerator, denominator, formula);
    if (value === null)
        return cybersecurityAssuranceIndexWord('derivedFormulaUnreadable', locale);
    const shown = formatCybersecurityAssuranceIndexDerivedValue(value, locale);
    if (!formula)
        return shown;
    const text = locale === 'vi' || locale.startsWith('vi-') ? formula.vi : formula.en;
    return `${shown} — ${cybersecurityAssuranceIndexWord('derivedOnRead', locale, { formula: text })}`;
}
//# sourceMappingURL=cybersecurity-assurance-index-derived.js.map